<template>
  <div class="cal-bar">
    <div class="cal-bar__track">
      <button v-for="option in options" :key="option"
              :class="['cal-bar__btn', { 'cal-bar__btn--active': selected === option }]"
              @click="onOptionSelected(option)">
        {{ $t(`calendarPicker.${option}`) }}
      </button>
      <button v-if="enableCustom"
              :class="['cal-bar__btn cal-bar__btn--custom', { 'cal-bar__btn--active': selected === 'custom' }]"
              @click.stop="toggleCustom">
        <i class="fa-regular fa-calendar-range mr-25" style="font-size:.82rem"/>
        {{ $t('calendarPicker.custom') }}
      </button>
    </div>

    <transition name="cal-bar-fade">
      <div v-if="selected === 'custom'" class="cal-bar__range">
        <i class="fa-solid fa-arrow-right-arrow-left mr-50" style="font-size:.7rem;opacity:.5"/>
        {{ customRange.start | moment($t('timeFormat.date.format')) }}
        <span class="cal-bar__range-sep">&mdash;</span>
        {{ customRange.end | moment($t('timeFormat.date.format')) }}
      </div>
    </transition>

    <!-- Custom dropdown panel -->
    <transition name="cal-drop">
      <div v-if="showCustom" class="cal-drop" v-click-outside="closeCustom">
        <div class="cal-drop__header">
          <i class="fa-regular fa-calendar-days mr-50 cal-drop__header-icon"/>
          <span>{{ $t('calendarPicker.custom') }}</span>
          <button class="cal-drop__close" @click="closeCustom"><i class="fa-solid fa-xmark"/></button>
        </div>
        <div class="cal-drop__body">
          <calendar-picker-range v-model="customRange" @input="onCustomInput" :clearable="false"/>
        </div>
        <div class="cal-drop__footer">
          <button class="cal-drop__apply" @click="applyCustom">
            <i class="fa-solid fa-check mr-50"/>{{ $t('calendarPicker.custom') }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import CalendarPickerRange from '@/components/widgets/calendar/calendarPickerRange';

export default {
  name: 'CalendarBar',
  components: { CalendarPickerRange },
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._outsideHandler = (e) => { if (!el.contains(e.target)) binding.value(e) }
        setTimeout(() => document.addEventListener('click', el._outsideHandler), 0)
      },
      unbind(el) { document.removeEventListener('click', el._outsideHandler) },
    }
  },
  data: () => ({
    options: ['all', 'today', 'yesterday', 'thisWeek', 'lastWeek', 'last7Days', 'thisMonth', 'lastMonth', 'last30Days', 'thisYear'],
    selected: 'today',
    showCustom: false,
    customRange: { start: null, end: null },
    pendingRange: { start: null, end: null },
  }),
  props: {
    value: { default: null },
    initSelection: { default: 'today' },
    fromKey: { default: 'from' },
    untilKey: { default: 'until' },
    enableCustom: { default: () => true },
    activeOptions: { default: () => [] },
  },
  created() {
    const today = this.$moment().format('YYYY-MM-DD')
    this.customRange = { start: today, end: today }
    this.pendingRange = { start: today, end: today }
    if (this.activeOptions != null && this.activeOptions.length > 0) {
      this.options = this.options.filter(o => this.activeOptions.includes(o))
    }
    this.onOptionSelected(this.initSelection)
  },
  methods: {
    onOptionSelected(option) {
      this.selected = option
      this.showCustom = false
      const value = this.getValue(option)
      this.$emit('input', { [this.fromKey]: value.from, [this.untilKey]: value.until })
    },
    toggleCustom() {
      this.pendingRange = { ...this.customRange }
      this.showCustom = !this.showCustom
    },
    closeCustom() { this.showCustom = false },
    onCustomInput(val) { if (val) this.pendingRange = val },
    applyCustom() {
      this.customRange = { ...this.pendingRange }
      this.selected = 'custom'
      this.showCustom = false
      this.$emit('input', { [this.fromKey]: this.customRange.start, [this.untilKey]: this.customRange.end })
    },
    getValue(option) {
      const m = this.$moment
      switch (option) {
        case 'all':       return { from: m().subtract(30, 'years').format('YYYY-MM-DD'), until: m().format('YYYY-MM-DD') }
        case 'today':     return { from: m().format('YYYY-MM-DD'), until: m().format('YYYY-MM-DD') }
        case 'yesterday': return { from: m().subtract(1,'days').format('YYYY-MM-DD'), until: m().subtract(1,'days').format('YYYY-MM-DD') }
        case 'thisWeek':  return { from: m().startOf('week').format('YYYY-MM-DD'), until: m().endOf('week').format('YYYY-MM-DD') }
        case 'lastWeek':  return { from: m().subtract(1,'week').startOf('week').format('YYYY-MM-DD'), until: m().subtract(1,'week').endOf('week').format('YYYY-MM-DD') }
        case 'last7Days': return { from: m().subtract(7,'days').format('YYYY-MM-DD'), until: m().format('YYYY-MM-DD') }
        case 'thisMonth': return { from: m().startOf('month').format('YYYY-MM-DD'), until: m().endOf('month').format('YYYY-MM-DD') }
        case 'lastMonth': return { from: m().subtract(1,'month').startOf('month').format('YYYY-MM-DD'), until: m().subtract(1,'month').endOf('month').format('YYYY-MM-DD') }
        case 'last30Days':return { from: m().subtract(30,'days').format('YYYY-MM-DD'), until: m().format('YYYY-MM-DD') }
        case 'thisYear':  return { from: m().startOf('year').format('YYYY-MM-DD'), until: m().endOf('year').format('YYYY-MM-DD') }
        case 'custom':    return { from: this.customRange.start, until: this.customRange.end }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* ── track ── */
.cal-bar { margin-bottom: 10px; position: relative; }
.cal-bar__track {
  display: flex; flex-wrap: wrap; gap: 6px; justify-content: center;
  background: #f1f5f9; border-radius: 12px; padding: 5px 6px;
}
.cal-bar__btn {
  border: none; outline: none; cursor: pointer;
  background: transparent; color: #64748b;
  font-size: .78rem; font-weight: 600; line-height: 1;
  padding: 7px 13px; border-radius: 8px; white-space: nowrap;
  transition: all .18s ease;
}
.cal-bar__btn:hover:not(.cal-bar__btn--active) { background: rgba(115,103,240,.08); color: #7367f0; }
.cal-bar__btn--active {
  background: linear-gradient(135deg, #7367f0, #9f97f5); color: #fff; font-weight: 700;
  box-shadow: 0 3px 10px rgba(115,103,240,.3);
}

/* ── selected range display ── */
.cal-bar__range { display: flex; align-items: center; justify-content: center; margin-top: 8px; font-size: .8rem; font-weight: 600; color: #475569; }
.cal-bar__range-sep { margin: 0 6px; opacity: .4; }

/* ── dropdown panel ── */
.cal-drop {
  position: absolute; top: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  z-index: 9999; width: 620px; max-width: 96vw;
  background: #fff; border-radius: 16px;
  border: 1px solid rgba(0,0,0,.07);
  box-shadow: 0 16px 60px rgba(0,0,0,.14);
  overflow: hidden;
}
.cal-drop__header {
  display: flex; align-items: center; padding: 14px 18px;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-bottom: 1px solid #e2e8f0;
  font-size: .9rem; font-weight: 700; color: #334155; gap: 6px;
}
.cal-drop__header-icon { color: #7367f0; font-size: 1rem; }
.cal-drop__close { margin-left: auto; background: none; border: none; cursor: pointer; color: #94a3b8; padding: 2px 6px; border-radius: 6px; transition: background .15s, color .15s; }
.cal-drop__close:hover { background: rgba(0,0,0,.06); color: #475569; }
.cal-drop__body { padding: 18px 20px 10px; }
.cal-drop__footer { padding: 10px 20px 16px; display: flex; justify-content: flex-end; }
.cal-drop__apply {
  border: none; cursor: pointer; border-radius: 10px; padding: 9px 22px;
  background: linear-gradient(135deg, #7367f0, #9f97f5); color: #fff;
  font-size: .85rem; font-weight: 700;
  box-shadow: 0 4px 14px rgba(115,103,240,.35);
  transition: box-shadow .2s, transform .15s;
}
.cal-drop__apply:hover { box-shadow: 0 6px 20px rgba(115,103,240,.45); transform: translateY(-1px); }

/* ── transitions ── */
.cal-bar-fade-enter-active, .cal-bar-fade-leave-active { transition: opacity .25s, transform .25s; }
.cal-bar-fade-enter, .cal-bar-fade-leave-to { opacity: 0; transform: translateY(-4px); }
.cal-drop-enter-active, .cal-drop-leave-active { transition: opacity .2s, transform .2s; }
.cal-drop-enter, .cal-drop-leave-to { opacity: 0; transform: translateX(-50%) translateY(-8px); }
</style>

<style lang="scss">
/* dark */
.dark-layout .cal-bar__track { background: #1e2a3e; }
.dark-layout .cal-bar__btn { color: #94a3b8; }
.dark-layout .cal-bar__btn:hover:not(.cal-bar__btn--active) { background: rgba(115,103,240,.15); color: #a78bfa; }
.dark-layout .cal-bar__btn--active { background: linear-gradient(135deg, #5a4fd4, #7367f0); }
.dark-layout .cal-bar__range { color: #b4b7bd; }
.dark-layout .cal-drop {
  background: #1e293b; border-color: rgba(255,255,255,.08);
  box-shadow: 0 16px 60px rgba(0,0,0,.5);
}
.dark-layout .cal-drop__header { background: linear-gradient(135deg, #0f172a, #1e293b); border-color: rgba(255,255,255,.08); color: #e2e8f0; }
.dark-layout .cal-drop__close { color: #64748b; }
.dark-layout .cal-drop__close:hover { background: rgba(255,255,255,.06); color: #94a3b8; }

/* v-calendar inside dark */
.dark-layout .cal-drop .vc-container,
.dark-layout .cal-drop .vc-pane-container { background: transparent !important; border: none !important; }
.dark-layout .cal-drop .vc-header,
.dark-layout .cal-drop .vc-arrows-container { background: transparent !important; }
.dark-layout .cal-drop .vc-title,
.dark-layout .cal-drop .vc-arrow { color: #e2e8f0 !important; }
.dark-layout .cal-drop .vc-weekday { color: #64748b !important; }
.dark-layout .cal-drop .vc-day-content { color: #cbd5e1 !important; }
.dark-layout .cal-drop .vc-day-content:hover { background: rgba(115,103,240,.25) !important; color: #fff !important; }
.dark-layout .cal-drop .vc-highlight { background: #7367f0 !important; }
.dark-layout .cal-drop .is-today .vc-day-content { color: #a78bfa !important; font-weight: 700; }
.dark-layout .cal-drop .vc-highlight-content-solid { color: #fff !important; }
.dark-layout .cal-drop .vc-highlight-content-light { color: #a78bfa !important; }
</style>
